import { useLanguage } from "@/i18n/index";

/**
 * Unified Status Badge - Single status field for patient
 * Values: STABLE, TO_MONITOR, CRITICAL, HOSPITALIZED
 * Also accepts alert levels: ROUGE, ORANGE, VERT (auto-mapped)
 * 
 * NOTE: Alert statuses (RESOLVED, VALIDATED, etc.) should NOT be passed here.
 * This badge is for PATIENT status, not alert status.
 */
export function StatusBadge({ status, size = "sm", testId }) {
  const { t } = useLanguage();

  // Normalize alert levels to patient status
  // Only map CHECK-IN alert levels, NOT alert workflow statuses
  const normalizeStatus = (s) => {
    const mapping = {
      // Check-in alert levels to patient status
      ROUGE: "CRITICAL",
      ORANGE: "TO_MONITOR",
      VERT: "STABLE",
      JAUNE: "STABLE",
      // Patient statuses (pass through)
      CRITICAL: "CRITICAL",
      OPEN_ALERT: "OPEN_ALERT",
      TO_MONITOR: "TO_MONITOR",
      STABLE: "STABLE",
      HOSPITALIZED: "HOSPITALIZED",
      HOSPITALISE: "HOSPITALIZED", // French version
      DECEDE: "DECEASED",
      // Alert workflow statuses should NOT change patient status display
      // If these are passed, return null to indicate error
      NEW: "CRITICAL", // An alert being NEW means patient needs attention
      IN_PROGRESS: null, // Should not be used for patient status
      ESCALATED: "CRITICAL", // Escalated = critical situation
      RESOLVED: null, // Should not be used for patient status
      VALIDATED: null, // Should not be used for patient status
    };
    const result = mapping[s];
    // If null, it means an invalid status was passed - default to showing nothing special
    return result === null ? undefined : result;
  };

  const normalizedStatus = normalizeStatus(status) || status;

  const config = {
    STABLE: { 
      label: t("status.STABLE"), 
      className: "bg-emerald-50 text-emerald-700 border border-emerald-200/60",
      dot: "bg-emerald-500"
    },
    TO_MONITOR: { 
      label: t("status.TO_MONITOR"), 
      className: "bg-orange-50 text-orange-700 border border-orange-200/60",
      dot: "bg-orange-500"
    },
    OPEN_ALERT: {
      label: t("status.OPEN_ALERT") || "Alerte ouverte",
      className: "bg-amber-50 text-amber-800 border border-amber-200/70",
      dot: "bg-amber-500"
    },
    CRITICAL: { 
      label: t("status.CRITICAL"), 
      className: "bg-rose-50 text-rose-700 border border-rose-200/60",
      dot: "bg-rose-500"
    },
    HOSPITALIZED: { 
      label: "Hospitalisé", 
      className: "bg-violet-50 text-violet-700 border border-violet-200/60",
      dot: "bg-violet-500"
    },
    DECEASED: { 
      label: "Décédé", 
      className: "bg-slate-100 text-slate-700 border border-slate-300",
      dot: "bg-slate-500"
    },
  };

  const item = config[normalizedStatus] || config["STABLE"];
  const sizeClass = size === "lg" ? "text-sm px-3 py-1.5" : "text-xs px-2.5 py-1";

  return (
    <span
      data-testid={testId || `status-badge-${normalizedStatus?.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${item.className} ${sizeClass}`}
    >
      <span className={`w-2 h-2 rounded-full ${item.dot}`} />
      {item.label}
    </span>
  );
}

export function OpenAlertBadge({ count = 0, level, size = "sm", testId }) {
  const normalizedCount = Number(count || 0);
  if (normalizedCount <= 0) return null;

  const critical = ["CRITICAL", "ROUGE", "HIGH"].includes(level);
  const orange = ["ORANGE", "MODERATE"].includes(level);
  const config = critical
    ? { className: "bg-rose-50 text-rose-700 border border-rose-200/70", dot: "bg-rose-500", test: "critical" }
    : orange
      ? { className: "bg-amber-50 text-amber-700 border border-amber-200/70", dot: "bg-amber-500", test: "orange" }
      : { className: "bg-slate-50 text-slate-700 border border-slate-200", dot: "bg-slate-400", test: "open" };

  const sizeClass = size === "lg" ? "text-sm px-3 py-1.5" : "text-xs px-2.5 py-1";
  const plural = normalizedCount > 1 ? "s" : "";
  const text = `${normalizedCount} alerte${plural}${critical ? " critique" + plural : ""} à traiter`;

  return (
    <span
      data-testid={testId || `open-alert-badge-${config.test}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${config.className} ${sizeClass}`}
    >
      <span className={`w-2 h-2 rounded-full ${config.dot}`} />
      {text}
    </span>
  );
}

/**
 * AlertLevelBadge - DEPRECATED, use StatusBadge instead
 * Kept for backwards compatibility, maps to StatusBadge
 */
export function AlertLevelBadge({ level, size = "sm" }) {
  // Map old alert levels to new unified status
  const statusMap = {
    ROUGE: "CRITICAL",
    VERT: "STABLE",
    JAUNE: "STABLE",
    ORANGE: "TO_MONITOR",
    CRITICAL: "CRITICAL",
  };
  
  return <StatusBadge status={statusMap[level] || level} size={size} />;
}
